import { useState } from 'react';
import {
  Table,
  Button,
  Spacer,
  Spinner,
  Icon,
  CheckBox,
  ITableProps,
  withErrorBoundary,
} from '@oliasoft-open-source/react-ui-library';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import translations from 'src/internationalisation/translation-map.json';
import { ConnectedProps, connect } from 'react-redux';
import { pick } from 'lodash';
import { HierarchyType } from 'src/enums/compare-estimates';
import { selectUserTemplates, selectCompanyTemplates } from 'src/store/entities/templates/selectors';
import { settingEntity } from 'src/enums/settings';
import { useParams } from 'react-router';
import type { ChangeEvent } from 'react';
import { runConceptCampaign, removeConceptDesign, removeEstimate } from 'src/store/entities/campaigns/campaigns';
import { selectSubjectId } from 'store/entities/authorization/selectors';
import {
  ICampaignActiveEstimate,
  IDesignItem,
  ICampaignEstimate,
  ICampaignTemplateData,
  ITemplateMeta,
} from 'src/common/interfaces/campaigns.interfaces';
import { formatTimeToUI } from 'src/views/time-tracker/utils/date/dateUtils';
import { ITemplate } from 'src/common/interfaces/templates.interfaces';
import type { TRootState } from 'src/store/store-types';
import { getHeaderLabels } from '../utils/utils';

interface IHierarchyInputTable extends PropsFromRedux {
  filteredEstimates: ICampaignEstimate[];
  conceptDesigns: IDesignItem[];
  name: string;
  activeEstimates: ICampaignActiveEstimate[];
  subjectId: string | null;
  campaignId: string;
  templates: ITemplateMeta[] | undefined;
  setActiveTabIndex: (value: number) => void;
}

const HierarchyInputTable = ({
  filteredEstimates,
  removeEstimate,
  campaignId,
  templateOptions,
  removeConceptDesign,
  runConceptCampaign,
  name,
  activeEstimates,
  subjectId,
  conceptDesigns,
  templates,
  setActiveTabIndex,
}: IHierarchyInputTable) => {
  const { t } = useTranslation();
  const { company } = useParams();
  const [filters, setFilters] = useState<{ [key: string]: string }>({});
  const [selectedPage, setSelectedPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedTemplates, setSelectedTemplates] = useState<ICampaignTemplateData[]>([]);
  const handleOnClickConfirmSelection = () => {
    setSelectedTemplates([]);
    if (company) {
      const updatedSelectedTemplates = [
        ...selectedTemplates,
        ...conceptDesigns
          .filter((design) => design.datasetId === null)
          .map((design) => ({
            datasetId: null,
            value: '',
            projectId: design.projectId ?? '',
          })),
      ];
      runConceptCampaign({
        companyId: company,
        campaignId,
        conceptDesigns,
        name,
        templates: updatedSelectedTemplates,
      });
      setActiveTabIndex(1);
    }
  };

  const hasConceptDesigns = conceptDesigns?.length > 0;

  const headerLabels = getHeaderLabels(t, translations, hasConceptDesigns);

  const headerRow = headerLabels?.map(({ label, identifier }) => {
    let width;

    if (label === t(translations.compare_automationMethod)) {
      width = '20%';
    } else if (identifier === HierarchyType.UseExisting) {
      width = '4%';
    } else {
      width = 'auto';
    }
    return {
      value: label,
      width,
    };
  });

  const headerFilter = headerLabels?.map((header) => {
    const { identifier } = header;
    const filterValue = filters[identifier] ?? '';
    return header.identifier === HierarchyType.AutomationMethod || header.identifier === HierarchyType.UseExisting
      ? {}
      : {
          key: identifier,
          value: filterValue,
          type: 'Input',
          placeholder: t(translations.search),
          onChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
            setFilters((p) => ({
              ...p,
              [identifier]: ev.target.value,
            }));
          },
        };
  });

  const startIndex = (selectedPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const estimates: any[] = filteredEstimates?.length > 0 ? filteredEstimates : conceptDesigns;

  const filteredData = estimates?.filter((design: IDesignItem | ICampaignEstimate) =>
    Object.keys(filters).every(
      (key) =>
        filters[key] === '' || design[key as keyof typeof design]?.toLowerCase().includes(filters[key].toLowerCase()),
    ),
  );

  const rows = [
    ...filteredData?.map((estimate: IDesignItem | ICampaignEstimate, idx: number) => {
      let rowCells = Object.entries(
        pick(estimate, [
          HierarchyType.EstimateName,
          HierarchyType.DesignName || HierarchyType.EstimateName,
          HierarchyType.WellboreName,
          HierarchyType.WellName,
          HierarchyType.SiteName,
          HierarchyType.FieldName,
          HierarchyType.CountryName,
        ]),
      ).map((value) => ({
        value: value[1],
      }));

      if (conceptDesigns?.length > 0) {
        const additionalCell =
          estimate.projectId === null
            ? {
                options: templateOptions,
                value:
                  selectedTemplates.find((template) => template.datasetId === (estimate as IDesignItem).datasetId)
                    ?.value || null,
                type: 'Select',
                placeholder: t(translations.compare_selectRuleset),
                onChange: (ev: ChangeEvent<HTMLSelectElement>) => {
                  setSelectedTemplates((prev) => {
                    const index = prev.findIndex((item) => item.datasetId === (estimate as IDesignItem).datasetId);
                    if (index !== -1) {
                      prev[index].value = ev.target.value;
                      return [...prev];
                    } else {
                      return [
                        ...prev,
                        {
                          datasetId: (estimate as IDesignItem).datasetId,
                          value: ev.target.value,
                          projectId: '',
                        },
                      ];
                    }
                  });
                },
              }
            : {};
        const index = selectedTemplates.findIndex(
          (template) => template?.datasetId === (estimate as IDesignItem)?.datasetId,
        );
        const existingEstimate = templates?.find(
          (template) =>
            template?.datasetId === selectedTemplates[index]?.datasetId &&
            template?.templateId === selectedTemplates[index]?.value &&
            template?.subjectId === subjectId,
        );
        const checked = selectedTemplates?.find((item) => item?.projectId === existingEstimate?.projectId);

        // TODO: Fix this when ReactNode is accepted as a type for helpText in checkbox-component
        const checkBox: any = existingEstimate
          ? {
              value: (
                <CheckBox
                  checked={!!checked}
                  helpText={
                    (
                      <>
                        {t(translations.campaigns_clickToUseExistingSimulation)}
                        <br />
                        <br />
                        {t(translations.campaigns_lastSimulation)}: {formatTimeToUI(templates?.[index]?.created)}
                      </>
                    ) as unknown as string
                  }
                  onChange={() => {
                    const newProjectId = checked ? '' : existingEstimate.projectId;
                    setSelectedTemplates((prev) =>
                      prev.map((item) =>
                        item.datasetId === existingEstimate.datasetId ? { ...item, projectId: newProjectId } : item,
                      ),
                    );
                  }}
                />
              ),
            }
          : {};
        rowCells = [checkBox, additionalCell, ...rowCells];
      }

      return {
        cells: rowCells,
        actions: [
          {
            label: 'Delete',
            icon: 'minus',
            onClick: () =>
              conceptDesigns?.length > 0
                ? removeConceptDesign(campaignId, conceptDesigns[idx]?.datasetId, conceptDesigns[idx]?.projectId)
                : removeEstimate(filteredEstimates[idx].campaignsEstimatesId, filteredEstimates[idx].campaignId),
          },
          ...(activeEstimates?.length > 0 && filteredEstimates?.length > 0
            ? (() => {
                const matchingEstimate = filteredEstimates.find(
                  (fe) => fe.campaignsEstimatesId === (estimate as ICampaignEstimate).campaignsEstimatesId,
                );
                if (!matchingEstimate) return [];
                return [
                  {
                    icon: !matchingEstimate.simulation ? (
                      <Spinner colored tiny />
                    ) : (
                      <Icon color="var(--color-background-success)" icon="success" />
                    ),
                  },
                ];
              })()
            : []),
        ],
      };
    }),
  ];

  const filteredRows = rows.slice(startIndex, endIndex);

  const table: ITableProps['table'] = {
    testId: 'campaigns-table-estimates',
    fixedWidth: '100%',
    headers: [{ cells: headerRow }, { cells: headerFilter }],
    rows: filteredRows,
    footer: {
      pagination: {
        rowCount: rows?.length,
        selectedPage,
        rowsPerPage: {
          onChange: (evt) => setRowsPerPage(Number(evt.target.value)),
          options: [
            { label: `10 / ${t(translations.page)}`, value: 10 },
            { label: `20 / ${t(translations.page)}`, value: 20 },
            { label: `50 / ${t(translations.page)}`, value: 50 },
          ],
          value: rowsPerPage,
        },
        onSelectPage: setSelectedPage,
        small: true,
      },
    },
  };
  const hasNotRunInitialCalculation =
    conceptDesigns?.filter((design) => design.datasetId).length === selectedTemplates.length;

  return (
    <>
      <Table table={table} />
      {conceptDesigns?.length > 0 && (
        <>
          <Spacer />
          <Button
            testId="campaigns-button-confirm-selection"
            label={t(translations.confirmSelection)}
            colored
            disabled={!hasNotRunInitialCalculation}
            onClick={handleOnClickConfirmSelection}
          />
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: TRootState) => {
  const userTemplates = selectUserTemplates(state);
  const companyTemplates = selectCompanyTemplates(state);

  const subjectId = selectSubjectId(state.entities);

  const templateOptions = [
    {
      label: i18n.t(translations.userRulesets),
      type: 'Heading',
    },
    ...userTemplates.map((template: ITemplate, idx: number) => ({
      label: template.highLevel.name,
      value: `${settingEntity.USER}-${idx}`,
    })),
    {
      label: i18n.t(translations.companyRulesets),
      type: 'Heading',
    },
    ...companyTemplates?.map((template: ITemplate, idx: number) => ({
      label: template.highLevel.name,
      value: `${settingEntity.COMPANY}-${idx}`,
    })),
  ];

  return {
    templateOptions,
    subjectId,
  };
};

const mapDispatchToProps = {
  runConceptCampaign,
  removeConceptDesign,
  removeEstimate,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(HierarchyInputTable));

export { Container as HierarchyInputTable };
